import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const LearnMore = ({ onClaimAccount }) => {
  const ASSET_BASE = 'https://processed.portal.soot.com/playground-static-assets/';
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isLastSectionVisible, setIsLastSectionVisible] = useState(false);
  const lastSectionRef = useRef(null);
  
  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsLastSectionVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (lastSectionRef.current) {
      observer.observe(lastSectionRef.current);
    }
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const parallaxOffset = scrollPosition * 0.5;

  const handleClaimAccount = () => {
    window.location.href = 'https://app.soot.com/signup';
  };
  
  return (
    <div className="min-h-screen bg-[#2E2E2E] overflow-y-auto">
      {/* Hero Section */}
      <div className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 w-full h-full flex items-center justify-center"
          style={{ 
            transform: `scale(1.6) translateY(${parallaxOffset * 0.2}px)`,
            transformOrigin: 'center center'
          }}
        >
          <div className="w-full h-full relative overflow-hidden">
            <video 
              className="absolute w-full h-full object-contain"
              autoPlay 
              loop 
              muted 
              playsInline
            >
              <source src={`${ASSET_BASE}/sootworld_AQPHYWccXI4S4k19yvndSIVyDckspe6SJsDYCRDyOlVEjzm9sOsAWdWrwqRbhdBecJfYL1yT5qx7TnnSEFaLzNFrghJS9Or0mZPKoxg.mp4`} type="video/mp4" />
            </video>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-8">
          <motion.h1
            className="text-6xl font-bold text-[#ECFF0F] mb-8"
            style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            IT'S TIME TO
            <br />
            BREAK UP WITH
            <br />
            THE GRID
          </motion.h1>
          
          <motion.p 
            className="text-2xl text-gray-200 max-w-2xl mx-auto mt-8"
            style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            SOOT is the next evolution of the filesystem
          </motion.p>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-6xl mx-auto px-8 py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-center">
          <div className="col-span-1 space-y-6">
            <motion.h2 
              className="text-4xl font-medium text-white mb-8"
              style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              For the Last 40 Years...
            </motion.h2>
            <motion.p 
              className="text-xl text-gray-300 leading-relaxed"
              style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Interacting with files has meant being limited by endless lists. We scroll, we click, we type.
              Trapped in a two-dimensional paradigm that limits how we work, create, and connect.
            </motion.p>
          </div>
          <motion.div 
            className="col-span-1"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <video 
              className="w-full h-auto rounded-3xl shadow-2xl"
              autoPlay 
              loop 
              muted 
              playsInline
            >
              <source src={`${ASSET_BASE}/sootyintro4.mp4`} type="video/mp4" />
            </video>
          </motion.div>
        </div>
      </div>

      {/* Last Section with Claim Account */}
      <div 
        ref={lastSectionRef}
        className="min-h-screen bg-[#e6e6e6] py-32 px-8 flex flex-col items-center justify-center"
      >
        <motion.div
          className="max-w-4xl mx-auto text-center space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isLastSectionVisible ? 1 : 0, y: isLastSectionVisible ? 0 : 20 }}
        >
          <h2 
            className="text-4xl font-medium text-black mb-8"
            style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
          >
            SOOT is Different
          </h2>
          <p 
            className="text-xl text-gray-700 leading-relaxed mb-16"
            style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
          >
            SOOT represents a fundamental shift in how we interact with our digital world that's spatial, intuitive, and boundless.
            We're introducing a powerful, visual way to map your digital content, from hard drives to cloud storage, and everything in between.
          </p>
          
          <motion.button
            onClick={handleClaimAccount}
            className="px-8 py-3 bg-black text-white rounded-lg text-xl font-medium hover:bg-gray-900 transition-colors"
            style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
            whileHover={{ scale: 1.05 }}
          >
            Sign up for SOOT
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default LearnMore;